import { Button, CircularProgress } from "@mui/material";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import React, { useState } from "react";
import { useParams, useRevalidator } from "react-router-dom";
import { fetcher } from "services/api.service";
import useSWR from "swr";

// Printing in Mobile Safari is rough.  Printing via iframe can work, but the scale is not correct.
// Opening a new window and printing directly from there works, but the window will not close automatically.
// This function uses the latter approach and tries to close the window after a delay.
// Taken from:
// https://github.com/crabbly/Print.js/issues/44#issuecomment-1550317552
function printInWindowSafariMobileHack(url: string) {
  const printWindow = window.open(url);
  if (!printWindow) throw new Error("Failed to open print window");
  printWindow.onload = () => {
    // Note using a zero delay setTimeout here to workaround issue that even when putting
    // print() inside of onload iOS Safari will prompt that the page hasn't loaded completely before print was called
    printWindow.setTimeout(() => {
      printWindow.focus();
      printWindow.print();

      // Try to automatically close the window after print
      // This is iOS Safari specific behavior we're relying on here. Essentially, there's not a good callback
      // currently to detect that the print dialog closed because
      // 1) "afterprint" event is only supported if the top level page is HTML where as it's PDF likely in this case
      // 2) there doesn't appear to be any other events (like focus) that we could hook into to detect
      // This works in iOS because if you call window.close() while the print dialog is open nothing happens. This isn't
      // true to other browsers though. Because of this though we can just periodically try to close the window until it works.
      printWindow.setInterval(() => printWindow.close(), 300);
    }, 500);
  };
}

interface ShippingLabelData {
  url: string | null;
  status: string;
}

export function PrintLabelButton({ onClick, children }: { onClick?: () => void; children: React.ReactNode }) {
  const { enabled } = useFeatureFlag("revalidate_print_label_button");
  const { batchId, itemId } = useParams();
  const { revalidate } = useRevalidator();
  const {
    data: labelData,
    isLoading: isLoadingURL,
    isValidating: isValidatingURL,
  } = useSWR(
    `/fulfillment_batches/${batchId}/items/${itemId}/shipping_label`,
    async (url: string) => fetcher<ShippingLabelData>(url),
    {
      // The label is created by a background job. The first response is PENDING, and the
      // screen has to ask again until the file is attached. useSWRImmutable forces
      // refreshInterval to 0, so that follow-up never happens.
      refreshInterval: (latestData?: ShippingLabelData) => (latestData?.status === "PENDING" ? 1000 : 0),
      dedupingInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const { data: imageURL, isLoading: isLoadingImage } = useSWR(
    labelData?.url ?? null,
    async (url: string) => {
      if (enabled) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch label PDF (${response.status})`);
        return URL.createObjectURL(await response.blob());
      } else {
        const response = await fetch(url);
        revalidate();
        return URL.createObjectURL(await response.blob());
      }
    },
    { shouldRetryOnError: true, errorRetryCount: 10, dedupingInterval: 2000 },
  );

  const waitingForLabelUrl =
    !labelData?.url && (isLoadingURL || isValidatingURL || labelData?.status === "PENDING" || !labelData);
  const isLoading = waitingForLabelUrl || isLoadingImage;

  const [showContinue, setShowContinue] = useState(false);
  return (
    <>
      <Button
        onClick={() => {
          if (imageURL) printInWindowSafariMobileHack(imageURL);
          if (onClick) setShowContinue(true);
        }}
        disabled={!imageURL}
      >
        {isLoading ? (
          <>
            Retrieving Shipping Label...
            <CircularProgress size={20} sx={{ ml: 2 }} />
          </>
        ) : (
          children
        )}
      </Button>
      {showContinue && (
        <Button variant={"contained"} onClick={onClick}>
          Continue
        </Button>
      )}
    </>
  );
}
